/**
 * Централизованная модель типов карьерных переходов
 * Единый источник истины для всех компонентов
 */

// Основной enum для типов переходов
export enum TransitionType {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal', 
  CROSS_FUNCTIONAL = 'cross_functional'
}

// Русские названия типов переходов (единообразие с главной страницей)
export const TRANSITION_TYPE_LABELS = {
  [TransitionType.VERTICAL]: 'Вертикальный',
  [TransitionType.HORIZONTAL]: 'Горизонтальный', 
  [TransitionType.CROSS_FUNCTIONAL]: 'Межфункциональный'
} as const

// Альтернативные названия для поддержки обратной совместимости
export const TRANSITION_TYPE_ALIASES = {
  // Основные русские названия
  'Вертикальный рост': TransitionType.VERTICAL,
  'Вертикальный': TransitionType.VERTICAL,
  'Горизонтальные': TransitionType.HORIZONTAL,
  'Горизонтальный': TransitionType.HORIZONTAL,
  'Межфункциональные': TransitionType.CROSS_FUNCTIONAL,
  'Межфункциональный': TransitionType.CROSS_FUNCTIONAL,
  'Межфункциональный переход': TransitionType.CROSS_FUNCTIONAL,
  
  // Вариации русских названий
  'вертикальный': TransitionType.VERTICAL,
  'горизонтальный': TransitionType.HORIZONTAL,
  'межфункциональный': TransitionType.CROSS_FUNCTIONAL,
  
  // Английские названия
  'vertical': TransitionType.VERTICAL,
  'horizontal': TransitionType.HORIZONTAL,
  'cross_functional': TransitionType.CROSS_FUNCTIONAL,
  'cross-functional': TransitionType.CROSS_FUNCTIONAL,
  
  // Для обратной совместимости
  'Горизонтальный переход': TransitionType.HORIZONTAL, // старое название
  
  // Дополнительные алиасы для исправления ошибок в данных
  // Когда вместо типа записаны названия должностей - помечаем как неизвестные (будут попадать в fallback)
} as const

// Иконки для типов переходов
export const TRANSITION_TYPE_ICONS = {
  [TransitionType.VERTICAL]: 'TrendingUp',
  [TransitionType.HORIZONTAL]: 'ArrowRight', 
  [TransitionType.CROSS_FUNCTIONAL]: 'ArrowUpRight'
} as const

// Цвета для типов переходов
export const TRANSITION_TYPE_COLORS = {
  [TransitionType.VERTICAL]: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: 'text-green-600'
  },
  [TransitionType.HORIZONTAL]: {
    bg: 'bg-blue-50', 
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: 'text-blue-600'
  },
  [TransitionType.CROSS_FUNCTIONAL]: {
    bg: 'bg-orange-50',
    border: 'border-orange-200', 
    text: 'text-orange-800',
    icon: 'text-orange-600'
  }
} as const

// Тип для возможных значений типов переходов
export type TransitionTypeValue = TransitionType | keyof typeof TRANSITION_TYPE_ALIASES

// Функция нормализации типа перехода
export const normalizeTransitionType = (type: string | null | undefined): TransitionType => {
  if (!type) return TransitionType.HORIZONTAL // fallback по умолчанию
  
  // Прямое соответствие enum
  if (Object.values(TransitionType).includes(type as TransitionType)) {
    return type as TransitionType
  }
  
  // Поиск в алиасах
  if (type in TRANSITION_TYPE_ALIASES) {
    return TRANSITION_TYPE_ALIASES[type as keyof typeof TRANSITION_TYPE_ALIASES]
  }
  
  console.warn(`Неизвестный тип перехода: "${type}". Используется fallback: horizontal`)
  return TransitionType.HORIZONTAL
}

// Функция получения русского названия
export const getTransitionTypeLabel = (type: TransitionTypeValue): string => {
  const normalizedType = normalizeTransitionType(type as string)
  return TRANSITION_TYPE_LABELS[normalizedType]
}

// Функция получения CSS классов для типа
export const getTransitionTypeClasses = (type: TransitionTypeValue) => {
  const normalizedType = normalizeTransitionType(type as string)
  return TRANSITION_TYPE_COLORS[normalizedType]
}

// Все доступные типы переходов для селектов
export const ALL_TRANSITION_TYPES = Object.values(TransitionType)

// Интерфейс для данных перехода
export interface CareerTransition {
  id: string | number
  from_position_id: string | number
  to_position_id: string | number
  transition_type: string
  requirements?: string
  probability?: number
  from_position_title?: string
  to_position_title?: string
  from_department?: string
  to_department?: string
} 